import { useQuery } from "@apollo/client";
import { Big } from "big.js";
import dayjs from "dayjs";
import { useCallback, useMemo } from "react";
import { flexpa__FlexpaImportStatus, type GetEobsQueryV2Query, gql, ingestion__IngestionStatus } from "@medbillai/generated-graphql-types";
import { Flag } from "@medbillai/utils/flag";
import { errorHandling } from "../../../lib/apollo/utils";
import { useBoolVariation } from "../../../lib/flag";
import { computeWebAgentLinkStatusFromIngestions, FLEXPA_LINK_WAIT_TIME_MINUTES, WEB_AGENT_INGESTION_WAIT_TIME_MINUTES } from "../../accounts-link/utils/utils";
import { normalizeHealthDataEobs, normalizeHealthDataInvoices } from "./healthDataBill";
import type { NormalizedEob } from "./types";
import { parsePeriod } from "./utils";

// TODO: Move into its own useInsuranceLinkStatus file
const getInsuranceLinkStatus = gql(/* GraphQL */`
  query GetInsuranceLinkStatusQuery {
    me {
      id
      flexpaLinks {
        id
        active
        lastSyncedAt
        createdAt
        endpoint {
          displayName
          slug
        }
        imports(order: { startedAt: { dir: DESC } }, first: 1) {
          id
          status
        }
      }
      webAgentLinks {
        id
        active
        createdAt
        endpoint {
          type
          displayName
          slug
        }
        # TODO: Revert back to latestIngestion here whenever we trust that -
        # currently, it can include multiple ingestions per link, with no
        # guarantee which data belongs to which ingestion. This can lead to us
        # saying the import is still "in progress" even when the 
        latestExecution {
          ingestions {
            status
          }
        }
      }
      explanationOfBenefits {
        id
      }
    }
  }
`);

/*
  Fetches the normalized EOB data for the bill screen. It fetches the most
  recent EOBs linked to a user via 'me'. It also fetches the status of the
  Flexpa and WebAgent links to show the user if the data is loading or not.
*/
const getEobsQueryV2 = gql(/* GraphQL */`
  query GetEobsQueryV2(
    $inNetwork: Boolean
    $outOfNetwork: Boolean
    $denied: Boolean
    $first: Int
    $after: Int
  ) {
    queryExplanationOfBenefitsV2(
      inNetwork: $inNetwork
      outOfNetwork: $outOfNetwork
      denied: $denied
      first: $first
      after: $after
    ) {
      id
      title
      status
      created
      resourceId
      totalsByType {
        memberLiability {
          value
          currency
        }
      }
      decision {
        text
        coding {
          code
        }
      }
      item {
        id
        productOrService {
          text
          coding {
            code
            display
          }
        }
        reviewOutcome {
          decision {
            text
          }
          reason {
            text
          }
        }
      }
      total {
        category {
          coding {
            code
          }
        }
        amount {
          currency
          value
        }
      }
      patient {
        display
      }
      billablePeriod
      provider {
        display
      }
      facility {
        display
      }
      serviceDate
    }
  }
`);

/*
  Ingestion health-data read path (behind the `bills-from-health-data` flag).
  Returns the user's EOB projection rows verbatim; `data` is a `v2.EOB` blob
  parsed client-side via `normalizeHealthDataEobs`. The health-data surface has
  no server-side network/decision filtering or pagination, so the list-tab
  filter args are not forwarded here. The flag-off v1 path keeps its existing
  filtering untouched.
*/
const getUserEobsQuery = gql(/* GraphQL */`
  query GetUserEobs {
    queryUserEobs {
      key
      kind
      data
      enrichment
      updatedAt
    }
  }
`);

/*
  Ingestion health-data invoice read path (behind the same flag). Returns the
  user's invoice projection rows verbatim; `data` is a `v2.Invoice` blob parsed
  client-side via `normalizeHealthDataInvoices`. Merged with the EOB rows for
  the unified bills list.
*/
const getUserInvoicesQuery = gql(/* GraphQL */`
  query GetUserInvoices {
    queryUserInvoices {
      key
      kind
      data
      enrichment
      updatedAt
    }
  }
`);
export enum LoadingStatus {
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  IN_PROGRESS = "IN_PROGRESS",
}
interface InsuranceLink {
  linkId: string;
  displayName: string | undefined;
  slug: string | undefined;
  status: LoadingStatus;
}
export type InsuranceLinks = InsuranceLink[];
type Filter = {
  inNetwork?: boolean;
  outOfNetwork?: boolean;
  denied?: boolean;
  first?: number;
  after?: number;
};

/** Newest first; rows without a service date sort to the bottom. */
const byServiceDateDesc = (a: NormalizedEob, b: NormalizedEob): number => {
  const aTime = a.serviceDate ? a.serviceDate.getTime() : 0;
  const bTime = b.serviceDate ? b.serviceDate.getTime() : 0;
  return bTime - aTime;
};
export function useNormalizedEobs(filter?: Filter) {
  const useHealthData = useBoolVariation(Flag.BILLS_FROM_HEALTH_DATA, false);
  const v1 = useQuery(getEobsQueryV2, {
    skip: useHealthData,
    context: errorHandling("caller"),
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-and-network",
    variables: filter
  });
  const eobs = useQuery(getUserEobsQuery, {
    skip: !useHealthData,
    context: errorHandling("caller"),
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-and-network"
  });
  const invoices = useQuery(getUserInvoicesQuery, {
    skip: !useHealthData,
    context: errorHandling("caller"),
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-and-network"
  });
  const v1Normalized = useMemo(() => normalizeEobData(v1.data?.queryExplanationOfBenefitsV2), [v1.data]);
  const healthDataNormalized = useMemo(() => {
    const normalizedEobs = normalizeHealthDataEobs(eobs.data?.queryUserEobs, {
      inNetwork: filter?.inNetwork,
      outOfNetwork: filter?.outOfNetwork,
      denied: filter?.denied
    });
    // Invoices have no decision/network, so they appear only under "All".
    const hasFilter = filter?.inNetwork || filter?.outOfNetwork || filter?.denied;
    const normalizedInvoices = hasFilter ? [] : normalizeHealthDataInvoices(invoices.data?.queryUserInvoices);
    return [...normalizedEobs, ...normalizedInvoices].sort(byServiceDateDesc);
  }, [eobs.data, invoices.data, filter?.inNetwork, filter?.outOfNetwork, filter?.denied]);
  const refetchHealthData = useCallback(async () => {
    const [eobResult] = await Promise.all([eobs.refetch(), invoices.refetch()]);
    return eobResult;
  }, [eobs.refetch, invoices.refetch]);

  // The health-data surface returns every row at once (no server-side
  // pagination), so `paginated` is false there and the consumer skips
  // `fetchMore`.
  return {
    networkStatus: useHealthData ? eobs.networkStatus : v1.networkStatus,
    loading: useHealthData ? eobs.loading || invoices.loading : v1.loading,
    data: useHealthData ? healthDataNormalized : v1Normalized,
    fetchMore: v1.fetchMore,
    paginated: !useHealthData,
    error: useHealthData ? eobs.error ?? invoices.error : v1.error,
    refetch: useHealthData ? refetchHealthData : v1.refetch
  };
}

/**
 * Normalizes the insurance links for the bill screen empty state.
 */
export function useInsuranceLinkStatus() {
  const {
    data: dataLinks,
    loading: loadingLinks,
    refetch
  } = useQuery(getInsuranceLinkStatus, {
    context: errorHandling("caller")
  });
  const insuranceLinks: InsuranceLinks = useMemo(() => {
    // Logic for status mirrors getFlexpaLinkStatus && getWebLinkStatus
    // do not update unless you update those functions
    const flexpaLinks: InsuranceLink[] = dataLinks?.me?.flexpaLinks?.filter(link => link.active).map(link => {
      let status = link.imports?.[0]?.status;
      if (!link?.lastSyncedAt) {
        if (dayjs().diff(link.createdAt, "minutes") < FLEXPA_LINK_WAIT_TIME_MINUTES) {
          status = flexpa__FlexpaImportStatus.IN_PROGRESS;
        } else {
          status = flexpa__FlexpaImportStatus.FAILED;
        }
      }
      return {
        linkId: link.id,
        displayName: link.endpoint.displayName ?? undefined,
        slug: link.endpoint.slug ?? undefined,
        status: status as unknown as LoadingStatus
      };
    }) || [];
    const webAgentLinks: InsuranceLink[] = dataLinks?.me?.webAgentLinks?.filter(link => link.active).map(link => {
      let status = computeWebAgentLinkStatusFromIngestions(link.latestExecution?.ingestions);
      if (!status) {
        if (dayjs().diff(link.createdAt, "minutes") > WEB_AGENT_INGESTION_WAIT_TIME_MINUTES) {
          status = ingestion__IngestionStatus.FAILED;
        } else {
          status = ingestion__IngestionStatus.IN_PROGRESS;
        }
      }
      return {
        linkId: link.id,
        displayName: link.endpoint.displayName ?? undefined,
        slug: link.endpoint.slug ?? undefined,
        status: status as unknown as LoadingStatus
      };
    }) || [];
    return [...flexpaLinks, ...webAgentLinks];
  }, [dataLinks?.me?.flexpaLinks, dataLinks?.me?.webAgentLinks]);
  return {
    loading: loadingLinks,
    dataInsuranceLinks: insuranceLinks,
    refetch
  };
}

/**
 * Normalizes the raw EOB data for use in bill screens.
 */
export function normalizeEobData(eobs: GetEobsQueryV2Query["queryExplanationOfBenefitsV2"]): NormalizedEob[] {
  if (!eobs) {
    return [];
  }
  return eobs.map(eob => {
    const memberLiability = eob.totalsByType?.memberLiability ?? eob.total?.find(t => t.category?.coding?.[0]?.code === "memberliability")?.amount;
    const totalDescription = eob.item?.reduce((acc, item) => {
      if (item?.productOrService?.text) {
        return `${acc + item.productOrService.text}, `;
      }
      return acc;
    }, "");
    const billablePeriod = parsePeriod(eob.billablePeriod);
    const serviceDate = eob.serviceDate || billablePeriod?.lower || eob.created;
    const nmData: NormalizedEob = {
      id: eob.id,
      resourceId: eob.resourceId,
      name: eob.patient?.display || null,
      status: eob.status,
      decision: eob.decision?.coding?.[0]?.code || null,
      totalCost: memberLiability ? {
        amount: Big(memberLiability.value ?? "0"),
        currency: memberLiability.currency ?? "USD"
      } : undefined,
      location: eob.provider?.display || eob.facility?.display || undefined,
      title: eob.title || undefined,
      serviceDate: serviceDate ? dayjs(serviceDate).toDate() : undefined,
      description: totalDescription || "No summary available"
    };
    return nmData;
  });
}